import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import { useSelector } from "react-redux";
import IconClose from "../../../../../assets/img/icon-close.svg";
import { Input, Select } from "../../../../../_metronic/_partials/controls";
import { i18n } from "../../../private/languageSelector";
import { buildStoreAddressSchema, cleanAddress, getCountryOptions, isSaudiArabia, toAddressForm } from "./storeAddress";

const TEXT_FIELDS = [
  {
    name: "buildingNumber",
    labelKey: "StoreAddress.BuildingNumber",
    placeholderKey: "StoreAddress.BuildingNumberPlaceholder",
    inputMode: "numeric",
    infoText: "e.g. Building 1234",
  },
  { name: "streetName", labelKey: "StoreAddress.StreetName", placeholderKey: "StoreAddress.StreetNamePlaceholder", infoText: "e.g. King Fahd Road" },
  { name: "district", labelKey: "StoreAddress.District", placeholderKey: "StoreAddress.DistrictPlaceholder", infoText: "e.g. Al Olaya" },
  { name: "city", labelKey: "StoreAddress.City", placeholderKey: "StoreAddress.CityPlaceholder", infoText: "e.g. Riyadh" },
  {
    name: "postalCode",
    labelKey: "StoreAddress.PostalCode",
    placeholderKey: "StoreAddress.PostalCodePlaceholder",
    inputMode: "numeric",
    infoText: "e.g. 12212",
  },
];

/**
 * Structured store address popup. Edits a copy of the saved address: onSave gets the trimmed
 * values, Cancel / close leaves the saved address untouched.
 */
export function StoreAddressDialog({ show, address, onCancel, onSave }) {
  const { language } = useSelector((state) => state.auth);
  const isArabic = language === "arabic";
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);

  return (
    <Modal show={show} onHide={onCancel} centered aria-labelledby="store-address-dialog-title" className="store-address-dialog">
      {/* Mounted only while open, so every open starts from the saved address */}
      {show && (
        <Formik
          initialValues={toAddressForm(address)}
          validationSchema={buildStoreAddressSchema()}
          validateOnMount={true}
          onSubmit={(values) => onSave(cleanAddress(values))}
        >
          {({ values, errors, touched, isValid, setFieldValue, setFieldTouched }) => (
            // Formik stops the submit event here, so it does not reach the Store form through the portal
            <Form className={`form form-label-right ${isArabic ? "rtl" : "ltr"}`} dir={isArabic ? "rtl" : "ltr"} noValidate>
              <Modal.Header className="w-100 bg-transparent border-0">
                <div className="d-flex justify-content-between w-100 align-items-center">
                  <button
                    type="button"
                    className="bg-transparent border-0"
                    style={{ color: "rgba(130, 130, 130, 1)", fontWeight: 700 }}
                    onClick={onCancel}
                  >
                    {i18n("SubscriptionModals.CLOSE")}
                  </button>
                  <button
                    type="submit"
                    className={`bg-transparent border-0 ${isArabic ? "mr-3" : "ml-3"}`}
                    disabled={!isValid}
                    style={{ fontWeight: 700, color: isValid ? "#009EF7" : "rgba(45, 156, 219, 0.35)" }}
                  >
                    {i18n("SubscriptionModals.CONTINUE")}
                  </button>
                </div>
              </Modal.Header>
              <Modal.Body className="mt-3">
                {TEXT_FIELDS.map((field, index) => (
                  <div className="form-group" key={field.name}>
                    <Field
                      name={field.name}
                      id={`store-address-${field.name}`}
                      component={Input}
                      type="text"
                      label={i18n(field.labelKey)}
                      placeholder={i18n(field.placeholderKey)}
                      aria-label={i18n(field.labelKey)}
                      aria-invalid={!!(touched[field.name] && errors[field.name])}
                      inputMode={field.inputMode}
                      maxLength={250}
                      autoFocus={index === 0}
                      isArabicStyle={true}
                    />
                    <div style={{ fontSize: "11px", fontWeight: 400, color: "rgba(189, 189, 189, 1)" }}>{field?.infoText}</div>
                  </div>
                ))}
                <div className="form-group form-select">
                  <Field
                    name="country"
                    id="store-address-country"
                    component={Select}
                    label={i18n("StoreAddress.Country")}
                    aria-label={i18n("StoreAddress.Country")}
                    aria-invalid={!!(touched.country && errors.country)}
                    withFeedbackLabel={false}
                    onChange={(e) => {
                      setFieldValue("country", e.target.value);
                      setFieldTouched("country", true, false);
                      // Show format problems in what is already typed right away under the new rules
                      ["buildingNumber", "postalCode"].forEach((name) => values[name] && setFieldTouched(name, true, false));
                    }}
                  >
                    <option value="">{i18n("StoreAddress.SelectCountry")}</option>
                    {countryOptions.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Field>
                  {touched.country && errors.country && <div className="invalid-feedback d-block">{errors.country}</div>}
                </div>
              </Modal.Body>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
}
